import styled from 'styled-components';
import {
  Page,
  Container,
  Section,
  Eyebrow,
  Display,
  SectionTitle,
  Card,
  Grid,
  Mono,
  PrimaryButton,
  GhostButton,
  Check,
  CheckList,
  CtaBand,
  DISCORD_DM,
} from '../Layout/PageLayout';
import { DiscordIcon } from '../Icons/CustomIcons';

const PACKAGES = [
  {
    label: 'BASIC · STARTER',
    price: '$50',
    description: 'Small fixes and short scripting tasks.',
    features: ['Bug fixes and small changes to existing systems', 'Small new features'],
    turnaround: '24–48 h turnaround',
  },
  {
    label: 'STANDARD',
    price: '$100',
    featured: true,
    description: 'Medium-sized work, such as a complete system or a cleanup of existing code.',
    features: [
      'Complete systems written from scratch',
      'Features of medium complexity',
      'Performance optimization',
      'Documentation with every delivery',
    ],
    turnaround: '3–5 days turnaround',
  },
  {
    label: 'ADVANCED · HIGH SCOPE',
    from: true,
    price: '$200',
    description: 'Large requests that involve backend architecture or a long development time.',
    features: [
      'Complex custom systems',
      'Full backend architecture',
      'Performance work for large player counts',
      'Priority support and extra revisions',
    ],
    turnaround: '1–2 weeks for larger builds',
  },
];

const ARRANGEMENTS = [
  ['Revenue share', 'Possible for established projects with a clear scope.'],
  ['Robux payments', 'Accepted for some Roblox projects. It depends on the scope of the work.'],
  ['Monthly retainer', 'Available for live games and for projects with unusual requirements.'],
];

const PackageCard = styled(Card)`
  padding: 2.25rem;
  border-radius: 0.75rem;
  display: flex;
  flex-direction: column;
  background: ${(p) => (p.$featured ? 'var(--surface-3)' : 'var(--card)')};
  border-color: ${(p) => (p.$featured ? 'var(--brand)' : 'var(--border)')};

  ${CheckList} {
    margin: 0 0 2rem;
    flex-grow: 1;
  }
`;

const Price = styled.div`
  font-family: var(--font-display);
  font-size: 3.5rem;
  font-weight: 600;
  letter-spacing: -0.03em;
  line-height: 1;

  small {
    font-size: 1.375rem;
    font-weight: 500;
    color: var(--text-secondary);
    letter-spacing: 0;
  }
`;

const Badge = styled.span`
  font-family: var(--font-mono);
  font-size: 0.6875rem;
  font-weight: 500;
  padding: 0.25rem 0.625rem;
  border-radius: 0.25rem;
  background: var(--brand);
  color: var(--brand-ink);
`;

const CardFoot = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  border-top: 1px solid var(--border-accent);
  padding-top: 1.25rem;

  a {
    min-height: 2.75rem;
    padding: 0.6875rem 1.125rem;
    font-size: 0.875rem;
  }
`;

const Arrangement = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 2rem;
  padding: 1.75rem 0;
  border-top: 1px solid var(--border);

  &:last-child {
    border-bottom: 1px solid var(--border);
  }

  h3 {
    flex: 0 0 12.5rem;
    font-family: var(--font-sans);
    font-size: 1.125rem;
    letter-spacing: 0;
  }

  p {
    flex: 1 1 17.5rem;
  }
`;

const Prices = () => (
  <Page>
    <Section style={{ paddingBottom: '4rem' }}>
      <Container style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem 4rem', alignItems: 'flex-end' }}>
        <div style={{ flex: '1.4 1 35rem', minWidth: 0 }}>
          <Eyebrow>Services / pricing</Eyebrow>
          <Display style={{ margin: 0 }}>Three starting prices.</Display>
        </div>
        <p style={{ flex: '1 1 22.5rem', minWidth: 0, fontSize: '1.125rem' }}>
          These prices are a starting point. The final price depends on complexity and urgency, and also on how much
          system design the work requires.
        </p>
      </Container>
    </Section>

    <Section $tight style={{ paddingBottom: '6rem' }}>
      <Container>
        <Grid $min="20rem">
          {PACKAGES.map((pkg) => {
            const Button = pkg.featured ? PrimaryButton : GhostButton;
            return (
              <PackageCard key={pkg.label} $featured={pkg.featured}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
                  <Mono>{pkg.label}</Mono>
                  {pkg.featured && <Badge>MOST POPULAR</Badge>}
                </div>
                <Price>
                  {pkg.from && <small>from </small>}
                  {pkg.price}
                </Price>
                <p style={{ margin: '1.25rem 0 1.75rem' }}>{pkg.description}</p>
                <CheckList>
                  {pkg.features.map((f) => (
                    <li key={f}>
                      <Check accent={pkg.featured} />
                      {f}
                    </li>
                  ))}
                </CheckList>
                <CardFoot>
                  <Mono $dim>{pkg.turnaround}</Mono>
                  <Button to="/contact">Request</Button>
                </CardFoot>
              </PackageCard>
            );
          })}
        </Grid>
      </Container>
    </Section>

    <Section $band style={{ borderBottom: 0 }}>
      <Container style={{ display: 'flex', flexWrap: 'wrap', gap: '3rem 4rem' }}>
        <div style={{ flex: '1 1 22.5rem', minWidth: 0 }}>
          <Eyebrow>Long-term work</Eyebrow>
          <SectionTitle style={{ fontSize: 'clamp(2rem, 4vw, 2.75rem)', marginBottom: '1.125rem' }}>
            Payment options for long-term work.
          </SectionTitle>
          <p>
            A fixed price does not always suit an established game or an ongoing project. In these cases, other payment
            arrangements can be agreed.
          </p>
        </div>
        <div style={{ flex: '1.5 1 35rem', minWidth: 0 }}>
          {ARRANGEMENTS.map(([title, text]) => (
            <Arrangement key={title}>
              <h3>{title}</h3>
              <p>{text}</p>
            </Arrangement>
          ))}
        </div>
      </Container>
    </Section>

    <CtaBand
      spaced
      title="Do you need an exact estimate?"
      text="Send the scope of the project and your deadline. I will reply with a price."
    >
      <PrimaryButton as="a" href={DISCORD_DM} target="_blank" rel="noopener noreferrer">
        <DiscordIcon size={18} />
        Get an estimate on Discord
      </PrimaryButton>
    </CtaBand>
  </Page>
);

export default Prices;
